import React, { useState, useRef } from "react";
import {
  FaSpinner,
  FaUser,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
} from "react-icons/fa";
import { IoMdCall } from "react-icons/io";
import { IoMail } from "react-icons/io5";
import { PiCertificateFill } from "react-icons/pi";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CaptchaModal from "../Modals/CaptchaModal";

const Form = () => {
  const ISOCertificate = [
    { ISOCname: "ISO 9001:2015" },
    { ISOCname: "ISO 14001:2015" },
    { ISOCname: "ISO 45001:2018" },
    { ISOCname: "ISO 22000:2018" },
    { ISOCname: "ISO 27001:2013" },
    { ISOCname: "ISO 50001:2011" },
    { ISOCname: "ISO 13485:2016" },
    { ISOCname: "ISO 29990:2010" },
    { ISOCname: "ISO 20000-1:2018" },
  ];

  const ComplianceCertificate = [
    { CompCname: "GMP Compliance" },
    { CompCname: "IATF 16949:2016 Compliance" },
    { CompCname: "KOSHER Compliance" },
    { CompCname: "HACCP Compliance" },
    { CompCname: "CE Compliance" },
    { CompCname: "SA 8000 Compliance" },
    { CompCname: "ORGANIC Compliance" },
    { CompCname: "ROHS Compliance" },
    { CompCname: "HALAL Compliance" },
  ];

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    isoCertificates: [],
    complianceCertificates: [],
  });

  const [dropdownStates, setDropdownStates] = useState({
    iso: false,
    compliance: false,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [isCaptchaOpen, setIsCaptchaOpen] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);

  const formRef = useRef();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleDropdown = (type) => {
    setDropdownStates((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const handleCertificateChange = (certificateName, type) => {
    const fieldName =
      type === "iso" ? "isoCertificates" : "complianceCertificates";

    setFormData((prev) => {
      const currentCertificates = prev[fieldName];
      if (currentCertificates.includes(certificateName)) {
        return {
          ...prev,
          [fieldName]: currentCertificates.filter(
            (cert) => cert !== certificateName
          ),
        };
      } else {
        return {
          ...prev,
          [fieldName]: [...currentCertificates, certificateName],
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isCaptchaVerified) {
      toast.error("Please verify you are not a robot.");
      return;
    }

    const totalCertificates =
      formData.isoCertificates.length + formData.complianceCertificates.length;
    if (totalCertificates === 0) {
      toast.error("Please select at least one certificate.");
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        subject: [
          ...(formData.isoCertificates.length > 0
            ? [`ISO: ${formData.isoCertificates.join(", ")}`]
            : []),
          ...(formData.complianceCertificates.length > 0
            ? [`Compliance: ${formData.complianceCertificates.join(", ")}`]
            : []),
        ].join(" | "),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/form/create-inquiry`,
        submitData,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200 || response.status === 201) {
        setFormData({
          name: "",
          mobile: "",
          email: "",
          isoCertificates: [],
          complianceCertificates: [],
        });
        setDropdownStates({ iso: false, compliance: false });
        setSuccess(true);
      }
    } catch (error) {
      toast.error(
        error.response?.data.message || "Network error. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFocus = () => {
    if (!isCaptchaVerified) setIsCaptchaOpen(true);
  };

  const handleCaptchaVerify = () => {
    setIsCaptchaOpen(false);
    setIsCaptchaVerified(true);
  };

  const closeSuccessMessage = () => {
    setSuccess(false);
  };

  return (
    <div className="w-full relative">
      {/* Captcha Modal overlay */}
      {isCaptchaOpen && (
        <div
          className="absolute inset-0 z-50 bg-gray-50 flex items-center justify-center border border-gray-200 rounded-lg shadow-md p-6"
          style={{ minHeight: formRef.current?.offsetHeight || "400px" }}
        >
          <CaptchaModal onVerify={handleCaptchaVerify} />
        </div>
      )}

      {/* Success Message */}
      {success ? (
        <div className="relative text-center bg-gradient-to-br from-green-50 via-green-100 to-emerald-50 border-2 border-green-300 rounded-2xl p-6 md:p-8 shadow-xl w-full overflow-hidden">
          {/* Close Button */}
          <button
            onClick={closeSuccessMessage}
            className="absolute top-3 right-3 p-2 text-gray-500 hover:text-gray-700 hover:bg-white hover:bg-opacity-50 rounded-full transition-all duration-200 z-10"
            aria-label="Close"
          >
            <FaTimes size={16} />
          </button>

          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-4 left-4 w-8 h-8 border-2 border-green-400 rounded-full"></div>
            <div className="absolute top-8 right-8 w-6 h-6 border-2 border-green-300 rounded-full"></div>
            <div className="absolute bottom-6 left-8 w-4 h-4 bg-green-200 rounded-full"></div>
            <div className="absolute bottom-8 right-6 w-5 h-5 bg-green-300 rounded-full"></div>
          </div>

          <div className="relative z-10">
            <div className="text-5xl mb-4 animate-bounce">🎉</div>
            <div className="w-16 bg-gradient-to-r from-green-400 to-emerald-500 mx-auto mb-4 rounded-full"></div>

            <h2 className="text-green-700 font-bold text-xl md:text-xl mb-2">
              Inquiry Submitted Successfully!
            </h2>
            <p className="text-gray-600 text-sm md:text-base mb-2">
              Thank you for reaching out. Our team will contact you shortly.
            </p>

            <div className="bg-white bg-opacity-60 rounded-xl p-4 mb-4 border border-green-200">
              <p className="text-gray-600 text-sm italic">
                You can also connect with us anytime via{" "}
                <a
                  href="https://wa.me/919337233933"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-green-600 hover:text-green-700 underline transition-colors"
                >
                  WhatsApp (9337233933)
                </a>{" "}
                or{" "}
                <a
                  href="tel:+919337233933"
                  className="font-semibold text-blue-600 hover:text-blue-700 underline transition-colors"
                >
                  Call (9337233933)
                </a>{" "}
                for quick assistance.
              </p>
            </div>

            <button
              onClick={closeSuccessMessage}
              className="px-6 py-2 bg-gradient-to-r from-[#0070a8] to-[#045b87] text-white rounded-lg hover:from-[#1e8cbf] hover:to-[#1674a3] transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Submit Another Inquiry
            </button>
          </div>
        </div>
      ) : (
        <div ref={formRef}>
          <h2 className="font-bold text-lg md:text-xl text-center text-gray-800 mb-3">
            Quick Enquiry
          </h2>

          <form className="space-y-2 w-full" onSubmit={handleSubmit}>
            {/* Basic Input Fields */}
            {["name", "mobile", "email"].map((field) => (
              <div
                key={field}
                className="rounded-lg bg-gray-50 px-3 py-2 md:py-3 flex items-center gap-3 border border-gray-200 focus-within:border-[#29AAE1] transition-colors"
              >
                {field === "name" && (
                  <FaUser size={18} className="text-gray-500" />
                )}
                {field === "mobile" && (
                  <IoMdCall size={18} className="text-gray-500" />
                )}
                {field === "email" && (
                  <IoMail size={18} className="text-gray-500" />
                )}

                <input
                  type={field === "email" ? "email" : "text"}
                  name={field}
                  value={formData[field]}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  placeholder={
                    field === "name"
                      ? "Full Name here"
                      : field === "mobile"
                      ? "Mobile Number"
                      : "Enter Email ID (Optional)"
                  }
                  className="bg-transparent outline-none w-full text-[#0070a8] placeholder-gray-400 text-sm md:text-base"
                  required={field === "name" || field === "mobile"}
                />
              </div>
            ))}

            {/* ISO Certificate Dropdown */}
            <div className="relative">
              <div
                onClick={() => toggleDropdown("iso")}
                className="cursor-pointer rounded-lg bg-gray-50 px-3 py-4 flex items-center gap-3 border border-gray-200 hover:border-[#29AAE1] transition-colors"
              >
                <PiCertificateFill size={18} className="text-gray-500" />
                <span className="font-medium flex-1">
                  ISO Certificates{" "}
                  {formData.isoCertificates.length > 0 &&
                    `(${formData.isoCertificates.length} selected)`}
                </span>
                {dropdownStates.iso ? (
                  <FaChevronUp size={14} className="text-gray-500" />
                ) : (
                  <FaChevronDown size={14} className="text-gray-500" />
                )}
              </div>

              {dropdownStates.iso && (
                <div className="absolute top-full left-0 right-0 z-40 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                  {ISOCertificate.map((cert, index) => (
                    <div
                      key={index}
                      onClick={() =>
                        handleCertificateChange(cert.ISOCname, "iso")
                      }
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={formData.isoCertificates.includes(
                          cert.ISOCname
                        )}
                        onChange={() => {}}
                        className="w-4 h-4 text-[#0070a8] border-gray-300 rounded focus:ring-[#0070a8]"
                      />
                      <span className="text-sm text-gray-700">
                        {cert.ISOCname}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Compliance Certificate Dropdown */}
            <div className="relative">
              <div
                onClick={() => toggleDropdown("compliance")}
                className="cursor-pointer rounded-lg bg-gray-50 px-3 py-4 flex items-center gap-3 border border-gray-200 hover:border-[#29AAE1] transition-colors"
              >
                <PiCertificateFill size={18} className="text-gray-500" />
                <span className="font-medium flex-1">
                  Compliance Certificates{" "}
                  {formData.complianceCertificates.length > 0 &&
                    `(${formData.complianceCertificates.length} selected)`}
                </span>
                {dropdownStates.compliance ? (
                  <FaChevronUp size={14} className="text-gray-500" />
                ) : (
                  <FaChevronDown size={14} className="text-gray-500" />
                )}
              </div>

              {dropdownStates.compliance && (
                <div className="absolute top-full left-0 right-0 z-40 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                  {ComplianceCertificate.map((cert, index) => (
                    <div
                      key={index}
                      onClick={() =>
                        handleCertificateChange(cert.CompCname, "compliance")
                      }
                      className="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={formData.complianceCertificates.includes(
                          cert.CompCname
                        )}
                        onChange={() => {}}
                        className="w-4 h-4 text-[#0070a8] border-gray-300 rounded focus:ring-[#0070a8]"
                      />
                      <span className="text-sm text-gray-700">
                        {cert.CompCname}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !isCaptchaVerified}
              className="w-full bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white py-2 md:py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50"
            >
              {loading ? (
                <FaSpinner className="animate-spin mx-auto" />
              ) : (
                "Submit"
              )}
            </button>
          </form>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
};

export default Form;
